import {
  CheckCircle,
  Copy,
  Eye,
  Pencil,
  Trash2,
  TestTube2,
  Unlock,
  UserPlus,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

type ItemActionKind = 'view' | 'edit' | 'duplicate' | 'test' | 'delete' | 'enable' | 'disable' | 'unlock' | 'add' | 'setDefault';

export interface ItemAction {
  kind: ItemActionKind;
  label?: string;
  title?: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ItemActionsProps {
  actions: ItemAction[];
  variant: 'card' | 'table';
}

interface ActionPresentation {
  label: string;
  icon: LucideIcon;
  cardClassName: string;
  tableClassName: string;
}

const actionPresentation: Record<ItemActionKind, ActionPresentation> = {
  view: {
    label: 'View',
    icon: Eye,
    cardClassName: 'text-blue-600 hover:bg-blue-50',
    tableClassName: 'text-gray-400 hover:text-blue-600 hover:bg-blue-50',
  },
  edit: {
    label: 'Edit',
    icon: Pencil,
    cardClassName: 'text-gray-700 hover:bg-gray-100',
    tableClassName: 'text-gray-400 hover:text-gray-700 hover:bg-gray-100',
  },
  duplicate: {
    label: 'Duplicate',
    icon: Copy,
    cardClassName: 'text-gray-700 hover:bg-gray-100',
    tableClassName: 'text-gray-400 hover:text-gray-700 hover:bg-gray-100',
  },
  test: {
    label: 'Test',
    icon: TestTube2,
    cardClassName: 'text-purple-600 hover:bg-purple-50',
    tableClassName: 'text-gray-400 hover:text-purple-600 hover:bg-purple-50',
  },
  delete: {
    label: 'Delete',
    icon: Trash2,
    cardClassName: 'text-red-600 hover:bg-red-50',
    tableClassName: 'text-gray-400 hover:text-red-600 hover:bg-red-50',
  },
  enable: {
    label: 'Enable',
    icon: CheckCircle,
    cardClassName: 'text-green-600 hover:bg-green-50',
    tableClassName: 'text-green-500 hover:text-green-600 hover:bg-green-50',
  },
  disable: {
    label: 'Disable',
    icon: XCircle,
    cardClassName: 'text-orange-600 hover:bg-orange-50',
    tableClassName: 'text-gray-400 hover:text-orange-600 hover:bg-orange-50',
  },
  unlock: {
    label: 'Unlock',
    icon: Unlock,
    cardClassName: 'text-green-600 hover:bg-green-50',
    tableClassName: 'text-gray-400 hover:text-green-600 hover:bg-green-50',
  },
  add: {
    label: 'Add Peers',
    icon: UserPlus,
    cardClassName: 'text-blue-600 hover:bg-blue-50',
    tableClassName: 'text-gray-400 hover:text-blue-600 hover:bg-blue-50',
  },
  setDefault: {
    label: 'Set Default',
    icon: CheckCircle,
    cardClassName: 'text-green-600 hover:bg-green-50',
    tableClassName: 'text-gray-400 hover:text-green-600 hover:bg-green-50',
  },
};

export function ItemActions({ actions, variant }: ItemActionsProps) {
  return (
    <div className={`flex items-center ${variant === 'card' ? 'gap-2' : 'justify-end gap-2'}`}>
      {actions.map((action) => {
        const presentation = actionPresentation[action.kind];
        const Icon = presentation.icon;
        const label = action.label ?? presentation.label;
        const title = action.title ?? label;
        const baseClassName = variant === 'card'
          ? 'flex items-center gap-1 px-3 py-1 text-sm rounded disabled:cursor-not-allowed disabled:opacity-50'
          : 'p-1.5 rounded disabled:cursor-not-allowed disabled:opacity-50';
        const colorClassName = variant === 'card'
          ? presentation.cardClassName
          : presentation.tableClassName;

        return (
          <button
            key={action.kind}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className={`${baseClassName} ${colorClassName}`}
            title={title}
            aria-label={title}
          >
            <Icon className="w-4 h-4" />
            {variant === 'card' && label}
          </button>
        );
      })}
    </div>
  );
}
